import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  DatePicker,
  Modal,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  message,
  Spin,
  Tooltip,
  Divider
} from 'antd';
import {
  DollarOutlined,
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SearchOutlined,
  EyeOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import moment from 'moment';
import dayjs from 'dayjs';
import { themeConfig } from '../config/theme';
import { getAllUsersAdvanceSummary, getAdvanceLogForUser } from '../services/advanceService';
import { listSalarySlipsForUser } from '../services/salarySlipService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AdvanceReport = () => {
  const [loading, setLoading] = useState(false);
  const [advanceSummaryData, setAdvanceSummaryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAdvanceEntries, setUserAdvanceEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [entriesModalVisible, setEntriesModalVisible] = useState(false);
  const [totalAdvanceWithUser, setTotalAdvanceWithUser] = useState(0);

  useEffect(() => {
    fetchAdvanceSummary();
  }, []);

  useEffect(() => {
    filterData();
  }, [advanceSummaryData, searchText]);

  const fetchAdvanceSummary = async () => {
    try {
      setLoading(true);
      const response = await getAllUsersAdvanceSummary();
      setAdvanceSummaryData(response.advanceSummaryData || []);
      setTotalAdvanceWithUser(response.totalAdvanceWithUser || 0);
    } catch (error) {
      console.error('Error fetching advance summary:', error);
      message.error('Failed to fetch advance summary');
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = advanceSummaryData;
    
    if (searchText) {
      filtered = filtered.filter(user => 
        user.FullName?.toLowerCase().includes(searchText.toLowerCase()) ||
        user.MobileNumber?.includes(searchText) ||
        user.Job?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    setFilteredData(filtered);
  };

  const handleUserClick = async (user) => {
    try {
      setSelectedUser(user);
      setEntriesLoading(true);
      setEntriesModalVisible(true);
      
      // Get all advance entries
      const advanceResponse = await getAdvanceLogForUser(user.MobileNumber);
      let allAdvanceEntries = advanceResponse.advanceLog || [];
      
      // Get all salary slips for the user to find the last one
      let lastSalarySlip = null;
      let lastSlipDate = null;
      let carriedForwardAmount = 0;
      
      try {
        const salarySlipsResponse = await listSalarySlipsForUser(user.MobileNumber);
        const salarySlips = salarySlipsResponse.salarySlips || [];
        
        if (salarySlips.length > 0) {
          // Sort salary slips by month (newest first)
          salarySlips.sort((a, b) => {
            const aDate = dayjs(a.MonthYear, 'YYYY-MM');
            const bDate = dayjs(b.MonthYear, 'YYYY-MM');
            return bDate.valueOf() - aDate.valueOf();
          });
          
          // Get the most recent salary slip
          lastSalarySlip = salarySlips[0];
          lastSlipDate = dayjs(lastSalarySlip.MonthYear, 'YYYY-MM').endOf('month');
          carriedForwardAmount = lastSalarySlip.AdvanceCarriedForward || 0;
        }
      } catch (error) {
        console.error('Error fetching salary slips:', error);
        // Continue without filtering if salary slips can't be fetched
      }
      
      // Filter entries to show only those after the last salary slip
      let filteredEntries = [];
      
      if (lastSlipDate) {
        // Filter advances that occurred after the last salary slip month
        filteredEntries = allAdvanceEntries.filter(entry => {
          const entryDate = dayjs(entry.Timestamp);
          return entryDate.isAfter(lastSlipDate);
        });
        
        // Add carried forward advance entry if it exists
        if (carriedForwardAmount > 0) {
          filteredEntries.unshift({
            Timestamp: lastSlipDate.toISOString(),
            Amount: carriedForwardAmount,
            Notes: `Carried Forward from ${lastSalarySlip.MonthYear}`,
            GivenByName: 'System',
            isCarriedForward: true
          });
        }
      } else {
        // If no salary slips found, show all entries
        filteredEntries = allAdvanceEntries;
      }
      
      // Sort by date (newest first)
      filteredEntries.sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));
      
      setUserAdvanceEntries(filteredEntries);
    } catch (error) {
      console.error('Error fetching user advance entries:', error);
      message.error('Failed to fetch advance entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (selectedUser) {
      handleUserClick(selectedUser);
    }
  };

  const getStatistics = () => {
    const totalUsers = filteredData.length;
    const usersWithAdvances = filteredData.filter(user => user.totalAdvanceWithUser > 0).length;
    const totalAdvanceWithUser = filteredData.reduce((sum, user) => sum + user.totalAdvanceWithUser, 0);

    return {
      totalUsers,
      usersWithAdvances,
      totalAdvanceWithUser
    };
  };

  const columns = [
    {
      title: 'User Details',
      key: 'userDetails',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.FullName}
          </div>
          <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
            {record.MobileNumber}
          </div>
          <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
            {record.Job} • {record.EmploymentType}
          </div>
        </div>
      ),
      sorter: (a, b) => a.FullName.localeCompare(b.FullName),
    },
    {
      title: 'Total Advance with User',
      dataIndex: 'totalAdvanceWithUser',
      key: 'totalAdvanceWithUser',
      render: (amount) => (
        <Text strong style={{ color: amount > 0 ? themeConfig.status.errorDark : themeConfig.text.muted }}>
          ₹{amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.totalAdvanceWithUser - b.totalAdvanceWithUser,
    },
    {
      title: 'Latest Advance',
      key: 'latestAdvance',
      render: (_, record) => (
        <div>
          {record.latestAdvanceDate ? (
            <>
              <div style={{ fontSize: '12px' }}>
                {moment(record.latestAdvanceDate).format('DD MMM YYYY')}
              </div>
              <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
                ₹{record.latestAdvanceAmount.toLocaleString()}
              </div>
            </>
          ) : (
            <Text type="secondary">No advances</Text>
          )}
        </div>
      ),
      sorter: (a, b) => {
        if (!a.latestAdvanceDate && !b.latestAdvanceDate) return 0;
        if (!a.latestAdvanceDate) return 1;
        if (!b.latestAdvanceDate) return -1;
        return new Date(a.latestAdvanceDate) - new Date(b.latestAdvanceDate);
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleUserClick(record)}
        >
          View Entries
        </Button>
      ),
    },
  ];

  const entryColumns = [
    {
      title: 'Date',
      dataIndex: 'Timestamp',
      key: 'date',
      render: (timestamp) => moment(timestamp).format('DD MMM YYYY'),
      sorter: (a, b) => new Date(a.Timestamp) - new Date(b.Timestamp),
    },
    {
      title: 'Amount',
      dataIndex: 'Amount',
      key: 'amount',
      render: (amount, record) => (
        <Text strong style={{ color: record.isCarriedForward ? themeConfig.status.info : themeConfig.status.errorDark }}>
          ₹{amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.Amount - b.Amount,
    },
    {
      title: 'Notes',
      dataIndex: 'Notes',
      key: 'notes',
      render: (notes, record) => (
        <span style={{ fontStyle: record.isCarriedForward ? 'italic' : 'normal' }}>
          {notes || '-'}
        </span>
      ),
      ellipsis: true,
    },
    {
      title: 'Given By',
      dataIndex: 'GivenByName',
      key: 'givenBy',
      render: (givenBy) => givenBy || '-',
    },
  ];

  const stats = getStatistics();

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            <FileTextOutlined style={{ marginRight: 8, color: themeConfig.status.info }} />
            Advance Report
          </Title>
          <Text type="secondary">
            Overview of all users and their advance amounts
          </Text>
        </div>

        {/* Statistics */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} justify="center">
            <Col span={8}>
              <Statistic
                title="Total Users"
                value={stats.totalUsers}
                prefix={<UserOutlined />}
                style={{ textAlign: 'center' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Users with Advances"
                value={stats.usersWithAdvances}
                prefix={<UserOutlined />}
                valueStyle={{ color: themeConfig.status.successDark }}
                style={{ textAlign: 'center' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Advance with User"
                value={stats.totalAdvanceWithUser}
                prefix={<DollarOutlined />}
                precision={2}
                valueStyle={{ color: themeConfig.status.errorDark }}
                formatter={(value) => `₹${value.toLocaleString()}`}
                style={{ textAlign: 'center' }}
              />
            </Col>
          </Row>
        </Card>

        {/* Search */}
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search by name, mobile number, or job..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        {/* Users Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="MobileNumber"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
          scroll={{ x: 800 }}
          style={{
            '& .ant-table-thead > tr > th': {
              backgroundColor: themeConfig.status.errorBg,
              color: 'var(--tertiary-text)',
              fontWeight: '600',
            },
            '& .ant-table-tbody > tr:nth-child(even) > td': {
              backgroundColor: themeConfig.background.tableRowEven,
            },
            '& .ant-table-tbody > tr:nth-child(odd) > td': {
              backgroundColor: themeConfig.background.tableRowOdd,
            },
            '& .ant-table-tbody > tr:hover > td': {
              backgroundColor: themeConfig.background.container,
            }
          }}
        />

        {/* User Advance Entries Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setEntriesModalVisible(false)}
                style={{ marginRight: 8 }}
              />
              <span>Advance Entries - {selectedUser?.FullName}</span>
            </div>
          }
          open={entriesModalVisible}
          onCancel={() => setEntriesModalVisible(false)}
          footer={null}
          width={1000}
        >

          <Table
            columns={entryColumns}
            dataSource={userAdvanceEntries.map((entry, index) => ({ 
              ...entry, 
              uniqueKey: entry.isCarriedForward ? `carried-forward-${index}` : (entry.Timestamp || `entry-${index}`)
            }))}
            rowKey={(record) => record.uniqueKey || record.Timestamp || `entry-${record.Amount}`}
            loading={entriesLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
            }}
            scroll={{ x: 600 }}
            style={{
              '& .ant-table-thead > tr > th': {
                backgroundColor: themeConfig.status.errorBg,
                color: themeConfig.colors.tertiaryText,
                fontWeight: '600',
              },
              '& .ant-table-tbody > tr:nth-child(even) > td': {
                backgroundColor: themeConfig.background.tableRowEven,
              },
              '& .ant-table-tbody > tr:nth-child(odd) > td': {
                backgroundColor: themeConfig.background.tableRowOdd,
              },
              '& .ant-table-tbody > tr:hover > td': {
                backgroundColor: themeConfig.background.container,
              }
            }}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default AdvanceReport;
